/// <amd-module name="Build/Extensions/Bitbucket/ConnectionSelection" />
import * as React from "react";

import { ConnectionList } from "Build/Extensions/Bitbucket/ConnectionList";
import * as Resources from "Build/Extensions/Bitbucket/Resources";
import {
    Actions,
    IServiceConnectionRetrieverService,
    ServiceConnectionInfo
} from "Build/Extensions/Connection-Services/ServiceConnectionRetrieverService";

import * as DesignerResources from "Build/Designer/Resources";

import { ITfsPageService } from "Tfs/Platform/Page";

import { format } from "VSS/Core/Util/String";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";

import { Button } from "VSSUI/Button";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { Constant, IServiceConnectionCreationService } from "Build/Extensions/Connection-Services/ServiceConnectionCreationService";

export interface Props {
    serviceName: string;
    sourceProvider: string;
    onSelectConnection: (serviceConnectionId: string) => void;
    onErrorMessage: (errorMessage: string) => void;
}

interface State {
    showConnections: boolean;
    serviceConnections: ServiceConnectionInfo[];
    projectId: string;
    isPanelExpanded: boolean;
}

export class ConnectionSelection extends VssComponent<Props, State, IVssComponentContext> {
    constructor(props: Props, context: IVssComponentContext) {
        super(props, context);

        const pageService = this.context.pageContext.getService<ITfsPageService>("ITfsPageService");
        const pageData = pageService.getData();

        this.state = {
            showConnections: false,
            serviceConnections: [],
            projectId: pageData!.project!.id,
            isPanelExpanded: false
        };
    }

    public componentDidMount(): void {
        const serviceConnectionRetrieverService = this.context.pageContext.getService<IServiceConnectionRetrieverService>("IServiceConnectionRetrieverService");
        serviceConnectionRetrieverService.subscribe(this._handleConnectionsUpdated, Actions.getServiceConnections);
        serviceConnectionRetrieverService.subscribe(this._handleError, Actions.error);
        serviceConnectionRetrieverService.updateServiceConnections(this.props.sourceProvider);
    }

    public componentWillUnmount(): void {
        const serviceConnectionRetrieverService = this.context.pageContext.getService<IServiceConnectionRetrieverService>("IServiceConnectionRetrieverService");
        serviceConnectionRetrieverService.unsubscribe(this._handleConnectionsUpdated, Actions.getServiceConnections);
        serviceConnectionRetrieverService.unsubscribe(this._handleError, Actions.error);
    }


    public render(): JSX.Element {
        return (
            <>
                {
                    this.renderContent()
                }
            </>
        );
    }

    private renderContent(): JSX.Element | null{
        const hasConnections: boolean = (this.state && this.state.serviceConnections && this.state.serviceConnections.length > 0);
        if (this.state.showConnections) {
            return (
                <div className="bitbucket-connection-selection">
                {
                    this._renderHeader()
                }
                {
                    hasConnections && (
                        <div className="section">
                            <div className="section-header title-s">{Resources.UseExistingConnection}</div>
                            <ConnectionList
                                showConnections={this.state.showConnections}
                                serviceConnections={this.state.serviceConnections}
                                getItemPrimaryText={this._getItemPrimaryText}
                                getItemSecondaryText={this._getItemSecondaryText}
                                onSelectConnection={this.props.onSelectConnection} />
                        </div>
                    )
                }
                {
                    AccessTokenAcquirer({
                        serviceName: this.props.serviceName,
                        hasExistingConns: hasConnections,
                        createAuthRequest: this._createAuthRequest
                    })
                }
                </div>
            );
        }
        else {
            return <Spinner size={SpinnerSize.large} label={Resources.GettingConnections}/>;
        }
    }

    private _createAuthRequest = (): void => {
        const service = this.context.pageContext.getService<IServiceConnectionCreationService>("IServiceConnectionCreationService");
        service.createAuthRequest(this.context, this.props.sourceProvider, this.state.projectId, false, this._handleError);
    }

    private _renderHeader(): JSX.Element {
        return <div className="header-section">
            <div className="subheader body-m secondary-text">{DesignerResources.NewPipeline}</div>
            <div className="header title-l">{DesignerResources.RepositorySelectorTitle}</div>
        </div>;
    }

    private _getItemPrimaryText = (connection: ServiceConnectionInfo): string => {
        if (connection) {
            return connection.name;
        }
        return Resources.Unknown;
    }

    private _getItemSecondaryText = (connection: ServiceConnectionInfo): string => {
        if (connection && connection.scheme) {
            if (connection.scheme === Constant.UserPassScheme) {
                return Resources.UserPass;
            } else {
                return connection.scheme;
            }
        }
        return Resources.Unknown;
    }

    private _handleConnectionsUpdated = (serviceConnections: ServiceConnectionInfo[]): void => {
        this.setState({
            showConnections: true,
            serviceConnections: serviceConnections,
        })
    }

    private _handleError = (e: any) => {
        if (e && e.message) {
            this.props.onErrorMessage(e.message);
        } else {
            this.props.onErrorMessage(e);
        }
    }
}

interface AccessTokenAcquirerProps {
    serviceName: string;
    hasExistingConns: boolean;
    createAuthRequest: () => void;
}

const AccessTokenAcquirer = (props: AccessTokenAcquirerProps) => {
    return <div className="section">
            <div className="connection-subheader title-s">{format(Resources.GrantPermission, props.serviceName)}</div>
            {
                <Button
                    className="authorize-button"
                    primary={!props.hasExistingConns}
                    text={Resources.Authorize}
                    onClick={props.createAuthRequest}
                />
            }
           </div>
};
